<template>
  <div class="grid scroll-div">
    <div class="grid-title bg-primary">
      <h4 class="text-bold text-white">Fabric Statistics</h4>
      <FabricCalculationInfo>
                <template #title>This table shows the Statistics of the Fabrics</template>
                <template #content>
                    <div class="calculation-info-container">
                        <div class="title">Avg Per Day</div>
                        <div class="description">The average number of main shirts made per day over a given timeframe.</div>
                        <code>
                            $totalMainShirts = sum($mainShirtsOrders); <br/>
                            $totalDaysInTimeframe = $totalNumberOfDaysInTheSpecifiedPeriod; <br/>
                            $result = $totalMainShirts / $totalDaysInTimeframe;
                        </code>
                    </div>
                    <div class="calculation-info-container">
                        <div class="title">Avg Per Available Days</div>
                        <div class="description">The average number of main shirts made per day when the fabric was available..</div>
                        <code>
                          $totalMainShirts = sum($mainShirtsOrders); <br/>
                            $availableDays = sum($availableDaysOfTheFabric); <br/>
                            $result = $totalMainShirts / $availableDays;
                        </code>
                    </div>
                    <div class="calculation-info-container">
                        <div class="title">Total Usage</div>
                        <div class="description">The total cuts made for fabric.</div>
                    </div>
                    <div class="calculation-info-container">
                        <div class="title">How Main, Cuff & Collar, and Contrast Are Counted</div>
                        <div class="description">
                          - If a shirt uses a single fabric, it is counted as <strong>1 Main</strong>,
                              and <strong>0 Cuff & Collar</strong> and <strong>0 Contrast</strong>.<br/>
                          - <strong>Cuff & Collar</strong> and Contrast are counted <strong>only when they use a fabric different from the Main fabric</strong>.<br/>
                          - These counts are based on the <strong>number of shirt orders</strong>, not on production steps.<br/>
                          - All values in this table reflect <strong>customer orders</strong>.
                        </div>
                    </div>
                    <div class="calculation-info-container">
                        <div class="title">Shirt Order</div>
                        <div class="description">The percentage of fabric cuts used for making shirts.</div>
                        <code>
                            $shirtCuts = sum($cutsUsedForShirts); <br/>
                            $totalCuts = sum($cutsMadeForFabric); <br/>
                            $result = ($shirtCuts / $totalCuts) * 100;
                        </code>
                    </div>
                    <div class="calculation-info-container">
                        <div class="title">Fabric Cutter</div>
                        <div class="description">The percentage of fabric cuts used by fabric cutters.</div>
                        <code>
                            $fabricCutterCuts = $cutsUsedByFabricCutters; <br/>
                            $totalCuts = sum($cutsMadeForFabric); <br/>
                            $result = ($fabricCutterCuts / $totalCuts) * 100;
                        </code>
                    </div>
                    <div class="calculation-info-container">
                        <div class="title">Collar + Cuff</div>
                        <div class="description">The percentage of fabric cuts used for cuffs and collars.</div>
                        <code>
                            $cuffAndCollarCuts = $cutsUsedForCuffsAndCollars; <br/>
                            $totalCuts = sum($cutsMadeForFabric); <br/>
                            $result = ($cuffAndCollarCuts / $totalCuts) * 100;
                        </code>
                    </div>
                    <div class="calculation-info-container">
                        <div class="title">Repair</div>
                        <div class="description">The percentage of fabric cuts used for repairs.</div>
                        <code>
                            $repairCuts = $cutsUsedForRepairs; <br/>
                            $totalCuts = sum($cutsMadeForFabric); <br/>
                            $result = ($repairCuts / $totalCuts) * 100;
                        </code>
                    </div>
                    <div class="calculation-info-container">
                        <div class="title">Books</div>
                        <div class="description">The percentage of fabric cuts used for books.</div>
                        <code>
                            $bookCuts = $cutsUsedForBooks; <br/>
                            $totalCuts = sum($cutsMadeForFabric); <br/>
                            $result = ($bookCuts / $totalCuts) * 100;
                        </code>
                    </div>
                    <div class="calculation-info-container">
                        <div class="title">Others</div>
                        <div class="description">The percentage of fabric cuts used for other purposes (e.g., masks, samples, remakes).</div>
                        <code>
                            $otherCuts = $cutsUsedForOtherPurposes; <br/>
                            $totalCuts = sum($cutsMadeForFabric); <br/>
                            $result = ($otherCuts / $totalCuts) * 100;
                        </code>
                    </div>
                    <div class="calculation-info-container">
                        <div class="title">Avg. Usage/Day*</div>
                        <div class="description">The average amount of fabric used per day.</div>
                        <code>
                            $mainUsage = sum($fabricsUsedForMainOrders); <br/>
                            $totalDaysInTimeframe = $totalNumberOfDaysInTheSpecifiedPeriod; <br/>
                            $result = $mainUsage / $totalDaysInTimeframe;
                        </code>
                    </div>
                    <div class="calculation-info-container">
                        <div class="title">Avg. Usage/Main*</div>
                        <div class="description">The average amount of fabric used per main shirt.</div>
                        <code>
                            $mainUsage = sum($fabricsUsedForMainShirtOrders); <br/>
                            $totalMainShirts = sum($mainShirtsOrders); <br/>
                            $result = $mainUsage / $totalMainShirts;
                        </code>
                    </div>
                    <div class="calculation-info-container">
                        <div class="title">Avg Cost/Main*</div>
                        <div class="description">The average cost of fabric used per main shirt.</div>
                        <code>
                            $fabricUnitPrice = $costPerUnitPriceOfFabric; <br/>
                            $result = $fabricUnitPrice * $averageUsagePerMain;
                        </code>
                    </div>
                </template>
      </FabricCalculationInfo>
    </div>
    <div class="grid-body p-0">
      <div class="col-md-4 p-14 text-left">
        <div class="row">
          <div class="col-md-4">
            <p-radio
              class="p-default"
              value="available"
              name="fabricFilter"
              @change="applyFabricFilter()"
              v-model="fabricFilter"
              color="success"
            >
              Available
            </p-radio>
          </div>
          <div class="col-md-4">
            <p-radio
              class="p-default"
              value="keep"
              name="fabricFilter"
              @change="applyFabricFilter()"
              v-model="fabricFilter"
              color="success"
            >
              Future=Keep
            </p-radio>
          </div>
          <div class="col-md-4">
            <p-radio
              class="p-default"
              value="all"
              name="fabricFilter"
              @change="applyFabricFilter()"
              v-model="fabricFilter"
              color="success"
            >
              All
            </p-radio>
          </div>
        </div>
      </div>
      <div class="col-md-6 p-14 text-left">
        <div class="row">
          <div class="col-md-2">
            <p-radio
              class="p-default"
              value="1d"
              name="timePeriod"
              @change="paramChanged()"
              v-model="serverParams.tableGraph.salePeriod"
              color="success"
            >
              1 Day
            </p-radio>
          </div>
          <div class="col-md-2">
            <p-radio
              class="p-default"
              value="1w"
              name="timePeriod"
              @change="paramChanged()"
              v-model="serverParams.tableGraph.salePeriod"
              color="success"
            >
              1 Week
            </p-radio>
          </div>
          <div class="col-md-2">
            <p-radio
              class="p-default"
              value="1m"
              name="timePeriod"
              @change="paramChanged()"
              v-model="serverParams.tableGraph.salePeriod"
              color="success"
            >
              1 Month
            </p-radio>
          </div>
          <div class="col-md-2">
            <p-radio
              class="p-default"
              value="3m"
              name="timePeriod"
              @change="paramChanged()"
              v-model="serverParams.tableGraph.salePeriod"
              color="success"
            >
              3 Months
            </p-radio>
          </div>
          <div class="col-md-2">
            <p-radio
              class="p-default"
              value="6m"
              name="timePeriod"
              @change="paramChanged()"
              v-model="serverParams.tableGraph.salePeriod"
              color="success"
            >
              6 Months
            </p-radio>
          </div>
          <div class="col-md-2">
            <p-radio
              class="p-default"
              value="1y"
              name="timePeriod"
              @change="paramChanged()"
              v-model="serverParams.tableGraph.salePeriod"
              color="success"
            >
              1 Year
            </p-radio>
          </div>
        </div>
      </div>
      <div class="col-md-2 p-14 text-left">
        <div class="row">
          <div class="col-md-2">
            <p-radio
              class="p-default"
              value="custom"
              name="timePeriod"
              @change="dateRangeChanged()"
              v-model="serverParams.tableGraph.salePeriod"
              color="success"
            >
              Custom
            </p-radio>
          </div>
        </div>
      </div>
      <div
        class="col-md-offset-8 col-md-4 p-14 text-left"
        v-if="serverParams.tableGraph.salePeriod == 'custom'"
      >
        <div class="row">
          <div class="col-md-6">
            <datepicker
              :clear-button="true"
              placeholder="From"
              v-model="serverParams.tableGraph.salePeriodStart"
              input-class="form-control"
              @input="dateRangeChanged()"
            >
            </datepicker>
          </div>
          <div class="col-md-6">
            <datepicker
              :clear-button="true"
              placeholder="To"
              v-model="serverParams.tableGraph.salePeriodEnd"
              input-class="form-control"
              @input="dateRangeChanged()"
            >
            </datepicker>
          </div>
        </div>
      </div>

      <table class="table table-bordered">
        <thead>
          <tr>
            <th colspan="2" class="fabric-header">Fabric</th>
            <th colspan="6" class="order-header">Orders</th>
            <th colspan="12" class="usage-header">
              Usage + Cost <span class="title-right">* incl Repair</span>
            </th>
          </tr>
          <tr class="sub-title">
            <th @click="sort('fabric_id')" class="fabric-header-2">
              ID
              <i class="fa fa-sort" aria-hidden="true"></i>
            </th>
            <th @click="sort('name')" class="fabric-header-2">
              Name
              <i class="fa fa-sort" aria-hidden="true"></i>
            </th>
            <th @click="sort('main_only')" class="order-header-2">
              Main
              <i class="fa fa-sort" aria-hidden="true"></i>
            </th>
            <th @click="sort('cuff_and_collar')" class="order-header-2">
              Collar + Cuff
              <i class="fa fa-sort" aria-hidden="true"></i>
            </th>
            <th @click="sort('contrast')" class="order-header-2">
              Contrast
              <i class="fa fa-sort" aria-hidden="true"></i>
            </th>
            <th @click="sort('total_shirt')" class="order-header-2">
              Total
              <i class="fa fa-sort" aria-hidden="true"></i>
            </th>
            <th @click="sort('shirt_avg_per_day')" class="order-header-2">
              Avg per day
              <i class="fa fa-sort" aria-hidden="true"></i>
            </th>
            <th
              @click="sort('shirt_avg_per_available_day')"
              class="order-header-2"
            >
              Avg per available days
              <i class="fa fa-sort" aria-hidden="true"></i>
            </th>
            <th @click="sort('total_cuts')" class="usage-header-2">
              Total Usage
              <i class="fa fa-sort" aria-hidden="true"></i>
            </th>
            <th @click="sort('shirt_usage')" class="usage-header-2">
              Shirt Order
              <i class="fa fa-sort" aria-hidden="true"></i>
            </th>
            <th @click="sort('fabric_cutter_usage')" class="usage-header-2">
              Fabric Cutter
              <i class="fa fa-sort" aria-hidden="true"></i>
            </th>
            <th @click="sort('cuff_collar_usage')" class="usage-header-2">
              Collar + Cuff
              <i class="fa fa-sort" aria-hidden="true"></i>
            </th>
            <th @click="sort('repair_usage')" class="usage-header-2">
              Repair
              <i class="fa fa-sort" aria-hidden="true"></i>
            </th>
            <th @click="sort('soft_and_neat')" class="usage-header-2">
              S &amp; N
              <i class="fa fa-sort" aria-hidden="true"></i>
            </th>
            <th @click="sort('oem')" class="usage-header-2">
              OEM
              <i class="fa fa-sort" aria-hidden="true"></i>
            </th>
            <th @click="sort('product_order')" class="usage-header-2">
              Product Order
              <i class="fa fa-sort" aria-hidden="true"></i>
            </th>
            <th @click="sort('other_usage')" class="usage-header-2">
              Others
              <i class="fa fa-sort" aria-hidden="true"></i>
            </th>
            <th @click="sort('avg_usage_per_day')" class="usage-header-2">
              Avg. Usage/Day*
              <i class="fa fa-sort" aria-hidden="true"></i>
            </th>
            <th
              @click="sort('avg_usage_per_shirt')"
              class="usage-header-orange"
            >
              Avg. Usage/Main*
              <i class="fa fa-sort" aria-hidden="true"></i>
            </th>
            <th @click="sort('avg_cost_per_shirt')" class="usage-header-orange">
              Avg. Cost/Main*
              <i class="fa fa-sort" aria-hidden="true"></i>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(fabric, index) in fabrics" :key="index">
            <td>
              <router-link :to="'/fabric/update/' + fabric.fabric_id">{{
                $Fabric.getCode(fabric.fabric_id)
              }}</router-link>
            </td>
            <td>
              <router-link :to="'/fabric/update/' + fabric.fabric_id"
                >{{ fabric.name }}
              </router-link>
            </td>
            <td class="text-right">{{ ezy.comma(fabric.main_only, 0) }}</td>
            <td class="text-right">
              {{ ezy.comma(fabric.cuff_and_collar, 0) }}
            </td>
            <td class="text-right">{{ ezy.comma(fabric.contrast, 0) }}</td>
            <td class="text-right">{{ ezy.comma(fabric.total_shirt, 0) }}</td>
            <td class="text-right">
              {{ ezy.comma(fabric.shirt_avg_per_day.toFixed(2)) }}
            </td>
            <td class="text-right">
              {{ ezy.comma(fabric.shirt_avg_per_available_day.toFixed(2)) }}
            </td>
            <td class="text-right">
              {{ ezy.comma(fabric.total_cuts.toFixed(2)) }} m
            </td>
            <td class="text-right">{{ fabric.shirt_usage.toFixed(2) }}%</td>
            <td class="text-right">
              {{ fabric.fabric_cutter_usage.toFixed(2) }}%
            </td>
            <td class="text-right">
              {{ fabric.cuff_collar_usage.toFixed(2) }}%
            </td>
            <td class="text-right">{{ fabric.repair_usage.toFixed(2) }}%</td>
            <td class="text-right">{{ fabric.soft_and_neat_usage.toFixed(2) }}%</td>
            <td class="text-right">{{ fabric.oem_usage.toFixed(2) }}%</td>
            <td class="text-right">{{ fabric.product_order_usage.toFixed(2) }}%</td>
            <td class="text-right">{{ fabric.other_usage.toFixed(2) }}%</td>
            <td class="text-right">
              {{ fabric.avg_usage_per_day.toFixed(2) }} m
            </td>
            <td class="text-right">
              {{ fabric.avg_usage_per_shirt.toFixed(2) }} m
            </td>
            <td class="text-right">
              {{ fabric.avg_cost_per_shirt.toFixed(2) }} EUR
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td>Total</td>
            <td>All fabrics</td>
            <td class="text-right">{{ ezy.comma(total.main_only, 0) }}</td>
            <td class="text-right">
              {{ ezy.comma(total.cuff_and_collar, 0) }}
            </td>
            <td class="text-right">{{ ezy.comma(total.contrast, 0) }}</td>
            <td class="text-right">{{ ezy.comma(total.total_shirt, 0) }}</td>
            <td class="text-right">
              {{ ezy.comma(total.shirt_avg_per_day.toFixed(2)) }}
            </td>
            <td class="text-right">
              {{ ezy.comma(total.shirt_avg_per_available_day.toFixed(2)) }}
            </td>
            <td class="text-right">
              {{ ezy.comma(total.total_cuts.toFixed(2)) }} m
            </td>
            <td class="text-right">{{ total.shirt_usage.toFixed(2) }}%</td>
            <td class="text-right">
              {{ total.fabric_cutter_usage.toFixed(2) }}%
            </td>
            <td class="text-right">
              {{ total.cuff_collar_usage.toFixed(2) }}%
            </td>
            <td class="text-right">{{ total.repair_usage.toFixed(2) }}%</td>
            <td class="text-right">{{ total.soft_and_neat_usage.toFixed(2) }}%</td>
            <td class="text-right">{{ total.oem_usage.toFixed(2) }}%</td>
            <td class="text-right">{{ total.product_order_usage.toFixed(2) }}%</td>
            <td class="text-right">{{ total.other_usage.toFixed(2) }}%</td>
            <td class="text-right">
              {{ total.avg_usage_per_day.toFixed(2) }} m
            </td>
            <td class="text-right">
              {{ total.avg_usage_per_shirt.toFixed(2) }} m
            </td>
            <td class="text-right">
              {{ total.avg_cost_per_shirt.toFixed(2) }} EUR
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import Datepicker from "vuejs-datepicker";
import FabricCalculationInfo from "./FabricCalculationInfo"
import moment from "moment";
export default {
  components: {
    Datepicker,
    FabricCalculationInfo
  },
  props: ["pageData", "serverParams"],
  data() {
    return {
      fabrics: [],
      notFilteredFabrics: [],
      sortType: "desc",
      fabricFilter: "all",
      total: {
        main_only: 0,
        cuff_and_collar: 0,
        contrast: 0,
        total_shirt: 0,
        shirt_avg_per_day: 0,
        shirt_avg_per_available_day: 0,
        total_cuts: 0,
        shirt_usage: 0,
        fabric_cutter_usage: 0,
        cuff_collar_usage: 0,
        repair_usage: 0,
        soft_and_neat_usage: 0,
        oem_usage: 0,
        product_order_usage: 0,
        other_usage: 0,
        avg_usage_per_day: 0,
        avg_usage_per_shirt: 0,
        avg_cost_per_shirt: 0,

        // Availabilities
        total_days: 0,
        available_days: 0,
        total_cost: 0,
        shirt_repair_usage: 0,
      },
    };
  },
  methods: {
    sort(column) {
      this.sortType = this.sortType === "asc" ? "desc" : "asc";
      this.fabrics = _.orderBy(this.fabrics, [column], [this.sortType]);
    },
    paramChanged() {
      this.$emit("changed", 1);
    },
    dateRangeChanged() {
      const tableGraph = this.serverParams.tableGraph;
      if (
        tableGraph.salePeriodStart !== null &&
        tableGraph.salePeriodEnd !== null
      ) {
        this.$emit("changed", 1);
      }
    },
    applyFabricFilter() {
      if (this.fabricFilter === "all") {
        this.fabrics = this.notFilteredFabrics;
      }
      if (this.fabricFilter === "available") {
        this.fabrics = this.notFilteredFabrics.filter((fabric) => {
          return (
            fabric.status === "available" ||
            fabric.status === "soon_sold_out_coming_back" ||
            fabric.status === "soon_sold_out_not_coming_back"
          );
        });
      }
      if (this.fabricFilter === "keep") {
        this.fabrics = this.notFilteredFabrics.filter((fabric) => {
          return fabric.future === "keep";
        });
      }
      this.computeTotals();
    },
    resetTotals() {
      this.total = {
        main_only: 0,
        cuff_and_collar: 0,
        contrast: 0,
        total_shirt: 0,
        shirt_avg_per_day: 0,
        shirt_avg_per_available_day: 0,
        total_cuts: 0,
        shirt_usage: 0,
        fabric_cutter_usage: 0,
        cuff_collar_usage: 0,
        repair_usage: 0,
        soft_and_neat_usage: 0,
        oem_usage: 0,
        product_order_usage: 0,
        other_usage: 0,
        avg_usage_per_day: 0,
        avg_usage_per_shirt: 0,
        avg_cost_per_shirt: 0,

        // Availabilities
        total_days: 0,
        available_days: 0,
        total_cost: 0,
        shirt_repair_usage: 0,
      };
    },
    computeTotals() {
      this.resetTotals();

      this.fabrics.forEach((fabric) => {
        this.total.available_days +=
          parseInt(fabric.available_days) !== 0
            ? parseInt(fabric.available_days)
            : 0;

        this.total.main_only += fabric.main_only;
        this.total.cuff_and_collar += fabric.cuff_and_collar;
        this.total.contrast += fabric.contrast;
        this.total.total_shirt += fabric.total_shirt;

        this.total.total_cuts += parseFloat(fabric.total_cuts);
        this.total.shirt_usage += parseFloat(fabric.cut_details.shirt);
        this.total.fabric_cutter_usage += parseFloat(
          fabric.cut_details.fabric_cutter
        );
        this.total.cuff_collar_usage += parseFloat(fabric.cut_details.c_and_c_cutting);
        this.total.repair_usage += parseFloat(fabric.cut_details.repair);
        this.total.soft_and_neat_usage += parseFloat(fabric.cut_details.soft_and_neat);
        this.total.oem_usage += parseFloat(fabric.cut_details.oem);
        this.total.product_order_usage += parseFloat(fabric.cut_details.product_order);

        const otherCuts =
          fabric.cut_details.mask +
          fabric.cut_details.sample +
          fabric.cut_details.remake;
        this.total.other_usage += otherCuts;

        const shirtFabricUsage =
          parseFloat(fabric.cut_details.shirt) +
          parseFloat(fabric.cut_details.repair) +
          parseFloat(fabric.cut_details.fabric_cutter) +
          parseFloat(fabric.cut_details.c_and_c_cutting);

        this.total.shirt_repair_usage += shirtFabricUsage;
        this.total.total_cost += shirtFabricUsage * fabric.fabric_unit_price;
      });

      const totalDaysInTimeframe = this.getTotalDaysInTimeframe();

      // Compute all averages
      this.total.shirt_avg_per_day =
        this.total.main_only / totalDaysInTimeframe;
      this.total.shirt_avg_per_available_day =
        (this.total.main_only * this.fabrics.length) /
        this.total.available_days;

      this.total.shirt_usage *= 100 / this.total.total_cuts;
      this.total.fabric_cutter_usage *= 100 / this.total.total_cuts;
      this.total.cuff_collar_usage *= 100 / this.total.total_cuts;
      this.total.repair_usage *= 100 / this.total.total_cuts;
      this.total.soft_and_neat_usage *= 100 / this.total.total_cuts;
      this.total.oem_usage *= 100 / this.total.total_cuts;
      this.total.product_order_usage *= 100 / this.total.total_cuts;
      this.total.other_usage *= 100 / this.total.total_cuts;

      this.total.avg_usage_per_day =
        this.total.shirt_repair_usage / totalDaysInTimeframe;
      this.total.avg_usage_per_shirt =
        this.total.shirt_repair_usage / this.total.main_only;
      this.total.avg_cost_per_shirt =
        this.total.total_cost / this.total.main_only;
    },
    getTotalDaysInTimeframe() {
      const salePeriod = this.serverParams.tableGraph.salePeriod;
      let totalDays = salePeriod;
      switch (salePeriod) {
        case "1d":
          totalDays = 1;
          break;
        case "1w":
          totalDays = 7;
          break;
        case "1m":
          totalDays = 30;
          break;
        case "3m":
          totalDays = 90;
          break;
        case "6m":
          totalDays = 180;
          break;
        case "1y":
          totalDays = 365;
          break;
        case "custom":
          let start = null;
          let end = null;
          if (
            typeof this.serverParams.tableGraph.salePeriodStart === "string"
          ) {
            start = moment(
              this.serverParams.tableGraph.salePeriodStart,
              "YYYY-MM-DD"
            );
            (end = moment(this.serverParams.tableGraph.salePeriodEnd)),
              "YYYY-MM-DD";
          } else {
            start = moment(this.serverParams.tableGraph.salePeriodStart);
            end = moment(this.serverParams.tableGraph.salePeriodEnd);
          }

          totalDays = end.diff(start, "days");
          break;
      }

      return totalDays;
    },
  },
  watch: {
    "pageData.fabricSaleAndUsageTable": function () {
      let fabricSaleAndUsageTable = this.pageData.fabricSaleAndUsageTable.data;
      let exchangeRate = this.pageData.fabricSaleAndUsageTable.exchange_rate;
      if (fabricSaleAndUsageTable) {
        const totalDaysInTimeframe = this.getTotalDaysInTimeframe();

        this.notFilteredFabrics = fabricSaleAndUsageTable.map((f) => {
          const available_days =
            parseInt(f.available_days) !== 0 ? parseInt(f.available_days) : 0;
          const cutDetails = f.cut_details;
          const totalCuts = parseFloat(f.total_cuts);
          const otherCuts =
            cutDetails.mask + cutDetails.sample + cutDetails.remake;

          f["total_shirt"] = f.main_only + f.cuff_and_collar + f.contrast;
          f["shirt_avg_per_day"] = f.main / totalDaysInTimeframe;
          f["shirt_avg_per_available_day"] =
            available_days !== 0 ? f.main / available_days : 0;

          /**
           * Fabric usages
           */

          var mainUsage =
            cutDetails.shirt +
            cutDetails.repair +
            cutDetails.fabric_cutter +
            cutDetails.c_and_c_cutting;
          f["shirt_usage"] =
            totalCuts !== 0 ? (100 * cutDetails.shirt) / totalCuts : 0;
          f["fabric_cutter_usage"] =
            totalCuts !== 0 ? (100 * cutDetails.fabric_cutter) / totalCuts : 0;
          f["cuff_collar_usage"] =
            totalCuts !== 0
              ? (100 * cutDetails.c_and_c_cutting) / totalCuts
              : 0;
          f["repair_usage"] =
            totalCuts !== 0 ? (100 * cutDetails.repair) / totalCuts : 0;
          f["soft_and_neat_usage"] =
            totalCuts !== 0 ? (100 * cutDetails.soft_and_neat) / totalCuts : 0;

          f["oem_usage"] =
            totalCuts !== 0 ? (100 * cutDetails.oem) / totalCuts : 0;

          f["product_order_usage"] =
            totalCuts !== 0 ? (100 * cutDetails.product_order) / totalCuts : 0;

          f["other_usage"] =
            totalCuts !== 0 ? (100 * otherCuts) / totalCuts : 0;

          f["shirt_repair_usage"] = mainUsage;
          f["avg_usage_per_day"] = mainUsage / totalDaysInTimeframe;
          f["avg_usage_per_shirt"] = f.main !== 0 ? mainUsage / f.main : 0;

          let costPerUnit = parseFloat(f.default_unit_price) / 100;
          if (f.default_unit === "y") {
            costPerUnit /= 1.094;
          }

          f["fabric_unit_price"] = costPerUnit;
          if (f.default_currency === "THB") {
            f["fabric_unit_price"] /= exchangeRate.thb; // Convert to Dollar
            f["fabric_unit_price"] /= exchangeRate.eur; // Convert to Euro
          }

          f["avg_cost_per_shirt"] =
            f["avg_usage_per_shirt"] * f["fabric_unit_price"];

          return f;
        });

        this.applyFabricFilter();
      }
    },
  },
};
</script>

<style scoped>
.grid-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
}

.grid-title h4 {
  margin-top: 0;
}

.fabric-header {
  background-color: #674ea7;
}
.fabric-header-2 {
  background-color: #7d64be;
}
.order-header {
  background-color: #666666;
}
.order-header-2 {
  background-color: #7d7d7d;
}

.usage-header {
  background-color: #2c55cb;
}
.usage-header-orange {
  background-color: #ff6d01;
}
.sub-title th {
  font-size: 14px;
  font-weight: normal;
}
table {
  max-width: none;
  width: 2500px;
}
table tfoot {
  position: sticky;
  inset-block-end: 0;
}
table tfoot tr td {
  background-color: #5e72e4;
  color: #fff;
}
.title-right {
  float: right;
}
</style>
